"use client";
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

interface TerminalOutput {
  id: string;
  type: string;
  content: string;
  createdAt: string;
}

interface TerminalCommand {
  id: string;
  command: string;
  createdAt: string;
}

interface TerminalSession {
  id: string;
  status: string;
  cwd: string | null;
  createdAt: string;
  closedAt: string | null;
  commands: TerminalCommand[];
  outputs: TerminalOutput[];
}

interface Task {
  id: string;
  jobNo?: number;
  jobId?: string;
  worker: string;
  description?: string;
  status: string;
  retryCount: number;
  createdAt: string;
  result?: string;
}

interface TaskDetailModalProps {
  task: Task;
  onClose: () => void;
}

export default function TaskDetailModal({ task, onClose }: TaskDetailModalProps) {
  const [detailData, setDetailData] = useState<any>(null);
  const [terminalLogs, setTerminalLogs] = useState<TerminalSession[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    loadDetail();
    loadTerminalLogs();
  }, [task.id]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const loadDetail = async () => {
    try {
      setLoadingDetail(true);
      const res = await fetch(`/api/tasks/detail?id=${encodeURIComponent(task.id)}`);
      if (!res.ok) throw new Error(`status ${res.status}`);
      const data = await res.json();
      setDetailData(data);
    } catch (e: any) {
      console.error('failed to load detail', e);
    } finally {
      setLoadingDetail(false);
    }
  };

  const loadTerminalLogs = async () => {
    try {
      setLoadingLogs(true);
      const res = await fetch(`/api/tasks/${task.id}/terminal-logs`);
      const data = await res.json();
      setTerminalLogs(data.sessions || []);
    } catch (e: any) {
      console.error('failed to load terminal logs', e);
      setTerminalLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("vi-VN");
  };

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: "bg-yellow-500",
      running: "bg-blue-500",
      completed: "bg-green-500",
      failed: "bg-red-500",
      retry: "bg-orange-500",
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold text-white rounded ${colors[status] || "bg-gray-500"}`}>
        {status}
      </span>
    );
  };

  if (!mounted) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="bg-slate-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-slate-700 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Task Details
            </h2>
            <div className="flex gap-3 items-center flex-wrap">
              <span className="text-gray-400 text-sm">ID: <span className="text-white font-mono">{task.id.substring(0, 8)}</span></span>
              <span className="text-gray-400">•</span>
              {task.jobNo && <span className="text-gray-400 text-sm">Plan #{task.jobNo}</span>}
              {getStatusBadge(task.status)}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Task Info */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-white">Task Information</h3>
            <div className="bg-slate-700 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex gap-2">
                <span className="text-gray-400 w-32">Worker:</span>
                <span className="text-white font-mono">{task.worker}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-gray-400 w-32">Retries:</span>
                <span className="text-white">{task.retryCount}</span>
              </div>
              <div className="flex gap-2">
                <span className="text-gray-400 w-32">Created:</span>
                <span className="text-white">{formatDate(task.createdAt)}</span>
              </div>
              {task.description && (
                <div className="flex gap-2">
                  <span className="text-gray-400 w-32">Description:</span>
                  <span className="text-white flex-1">{task.description}</span>
                </div>
              )}
            </div>
          </div>

          {/* Result */}
          {task.result && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Result</h3>
              <pre className="bg-slate-900 text-gray-300 p-4 rounded-lg overflow-x-auto max-h-64 overflow-y-auto text-sm font-mono whitespace-pre-wrap">
                {task.result}
              </pre>
            </div>
          )}

          {/* Steps */}
          {!loadingDetail && detailData?.steps && detailData.steps.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Steps</h3>
              <ul className="space-y-2">
                {detailData.steps.map((s: any) => (
                  <li key={s.id} className="bg-slate-700 p-3 rounded text-white text-sm">
                    <span className="text-blue-400 font-semibold">{s.stepType}:</span> {s.content}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tools */}
          {!loadingDetail && detailData?.tools && detailData.tools.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Tools Used</h3>
              <ul className="space-y-2">
                {detailData.tools.map((t: any) => (
                  <li key={t.id} className="bg-slate-700 p-3 rounded text-white text-sm">
                    <span className="text-green-400 font-semibold">{t.toolName}</span> → {JSON.stringify(t.input)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Terminal Logs */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-white">Terminal Logs</h3>
            {loadingLogs ? (
              <div className="text-center text-gray-400 py-8">
                <svg className="w-8 h-8 mx-auto animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm mt-2">Loading logs...</span>
              </div>
            ) : terminalLogs.length > 0 ? (
              <div className="space-y-3">
                {terminalLogs.map((session) => (
                  <div key={session.id} className="bg-slate-900 rounded-lg overflow-hidden">
                    {/* Session Header */}
                    <div className="bg-slate-950 px-4 py-2 flex items-center justify-between border-b border-slate-700">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-400">Session:</span>
                        <span className="text-gray-300 font-mono">{session.id.substring(0, 8)}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-400">{session.cwd || '/'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`px-2 py-0.5 rounded ${
                          session.status === 'active' ? 'bg-green-900 text-green-300' :
                          session.status === 'closed' ? 'bg-gray-700 text-gray-300' :
                          'bg-yellow-900 text-yellow-300'
                        }`}>
                          {session.status}
                        </span>
                        <span className="text-gray-500">{formatDate(session.createdAt)}</span>
                      </div>
                    </div>
                    
                    {/* Terminal Content */}
                    <div className="p-4 font-mono text-xs max-h-96 overflow-y-auto">
                      {/* Commands */}
                      {session.commands && session.commands.length > 0 && (
                        <div className="space-y-2">
                          {session.commands.map((cmd) => (
                            <div key={cmd.id} className="text-green-400">
                              <span className="text-gray-500">$ </span>
                              {cmd.command}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Outputs */}
                      {session.outputs && session.outputs.length > 0 && (
                        <div className={session.commands?.length ? 'mt-2 space-y-1' : 'space-y-1'}>
                          {session.outputs.map((output) => (
                            <div 
                              key={output.id}
                              className={`whitespace-pre-wrap ${
                                output.type === 'stderr' ? 'text-red-300' :
                                output.type === 'stdout' ? 'text-gray-300' :
                                'text-blue-300'
                              }`}
                            >
                              {output.content}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {(!session.commands || session.commands.length === 0) && 
                       (!session.outputs || session.outputs.length === 0) && (
                        <div className="text-gray-500 italic">No terminal activity recorded</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-slate-900 p-8 rounded-lg text-center text-gray-500">
                Không có terminal logs
              </div>
            )}
          </div>

          {/* Sessions */}
          {!loadingDetail && detailData?.sessions && detailData.sessions.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Sessions</h3>
              <ul className="space-y-2">
                {detailData.sessions.map((s: any) => (
                  <li key={s.id} className="bg-slate-700 p-3 rounded text-white text-sm flex items-center justify-between">
                    <span>{s.tmuxSession} ({s.status})</span>
                    <button 
                      onClick={() => window.open(`/dashboard/terminal?session=${s.id}`, '_blank')}
                      className="text-blue-400 hover:text-blue-300 text-xs underline"
                    >
                      Open
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700 sticky bottom-0 bg-slate-800">
          <button
            onClick={onClose}
            className="w-full md:w-auto px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-md font-medium transition-colors"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  );

  console.log('📦 Creating portal for task to document.body:', document.body);
  return createPortal(modalContent, document.body);
}
